import { Typography, TextField, Box, Button } from '@mui/material'
import { IStockCardIngredients } from 'interfaces/IStockCard'
import { IIngredient } from 'interfaces/IIngredient'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { formatStockQuantity } from 'components/inventory/helpers'
import { clampDecimalPlaces } from 'utils/clampDecimalPlaces'

interface Props {
  stockCardIngredient: IStockCardIngredients
  inOutDirection: string
  ingredients: IIngredient[]
  outgoingDestination?: string
  onChange: (ingredientId: string, value: any, key: string) => void
  rowIdentifier?: string
  // optional labels to override the default copy for IN/OUT contexts
  qtyLabelIn?: string
  qtyLabelOut?: string
  disabled?: boolean
  isSuperuser?: boolean
  isConfirmed?: boolean
  isEditing?: boolean
  onRemove?: () => void
}

const FieldRow = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <Box display="flex" borderBottom="1px solid #eee" py={1.5} alignItems="center">
    <Typography style={{ width: '30%', fontWeight: 500, fontSize: '0.95rem', color: '#666' }}>
      {label}
    </Typography>
    <Box flexGrow={1}>
      {children}
    </Box>
  </Box>
)

export default function StockCardIngredientItem(props: Props) {
  const {
    stockCardIngredient,
    inOutDirection,
    ingredients,
    onChange,
    rowIdentifier,
    qtyLabelIn,
    qtyLabelOut,
    disabled,
    isConfirmed,
    isEditing,
    onRemove,
  } = props

  const isEditable = isEditing || !isConfirmed

  const currentStock =
    ingredients.find(
      (i) =>
        i.ingredientId?.toLowerCase() ===
        stockCardIngredient.ingredientId?.toLowerCase()
    )?.stock ?? 0

  const displayIngredientId =
    stockCardIngredient.ingredientId ||
    stockCardIngredient.ingredient?.ingredientId ||
    ''
  const displayIngredientName =
    stockCardIngredient.ingredientName ||
    stockCardIngredient.ingredient?.ingredientName ||
    ''

  const qtyLabel = inOutDirection === VALUE_IN ? (qtyLabelIn || 'Qty Mau Dimasukan') : (qtyLabelOut || 'Qty Diminta')
  const confirmedQtyLabel = inOutDirection === VALUE_IN ? 'Jumlah Diterima (kg)' : 'Jumlah Dikeluarkan (kg)'
  const currentStockLabel = 'Stok Sekarang'
  const changeIdentifier = rowIdentifier ?? stockCardIngredient.ingredientId

  return (
    <Box width="100%" py={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Item Bahan Baku
        </Typography>
        {onRemove && (
          <Button color="secondary" onClick={onRemove} size="small" variant="outlined">
            Hapus
          </Button>
        )}
      </Box>

      {isEditable ? (
        <Box>
          <FieldRow label="Nama Bahan">
            {isEditing ? (
              <LoadIngredientAutocomplete
                name={displayIngredientId}
                onChange={(e: IIngredient) => {
                  onChange(changeIdentifier, e, 'ingredient')
                }}
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{displayIngredientName}</Typography>
            )}
          </FieldRow>

          <FieldRow label={currentStockLabel}>
            {ingredients.length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(currentStock)} kg</Typography>
            )}
          </FieldRow>

          <FieldRow label={qtyLabel}>
            {isEditing ? (
              <TextField
                value={stockCardIngredient.quantity}
                onChange={(e) => onChange(changeIdentifier, clampDecimalPlaces(e.target.value), 'quantity')}
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                inputProps={{ step: '0.000000000001' }}
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardIngredient.quantity)}</Typography>
            )}
          </FieldRow>

          <FieldRow label={confirmedQtyLabel}>
            <TextField
              required
              autoComplete="off"
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              inputProps={{ step: '0.000000000001' }}
              value={stockCardIngredient.confirmedQuantity ?? ''}
              onChange={(e) =>
                onChange(
                  changeIdentifier,
                  clampDecimalPlaces(e.target.value),
                  'confirmedQuantity'
                )
              }
              disabled={disabled}
              error={
                inOutDirection === VALUE_OUT &&
                Number(stockCardIngredient.confirmedQuantity) > currentStock
              }
              helperText={
                inOutDirection === VALUE_OUT &&
                  Number(stockCardIngredient.confirmedQuantity) > currentStock
                  ? 'Melebihi stok'
                  : ''
              }
            />
          </FieldRow>

          <FieldRow label="Kode Bahan">
            <Typography style={{ fontWeight: 600 }}>{displayIngredientId}</Typography>
          </FieldRow>
        </Box>
      ) : (
        <Box>
          <FieldRow label="Kode Bahan">
            <Typography style={{ fontWeight: 600 }}>{displayIngredientId}</Typography>
          </FieldRow>
          <FieldRow label="Nama Bahan">
            <Typography style={{ fontWeight: 600 }}>{displayIngredientName}</Typography>
          </FieldRow>
          <FieldRow label={currentStockLabel}>
            {ingredients.length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(currentStock)} kg</Typography>
            )}
          </FieldRow>
          <FieldRow label={qtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardIngredient.quantity ?? 0)} kg</Typography>
          </FieldRow>
          <FieldRow label={confirmedQtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardIngredient.confirmedQuantity)} kg</Typography>
          </FieldRow>
        </Box>
      )}
    </Box>
  )
}
